import Modal from '@layouts/Modal';
import Button from '@src/ui/elements/button/Button';
import ErrorMessage from '@src/ui/elements/form/ErrorMessage';
import Input from '@src/ui/elements/form/Input';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import React, { useEffect, useState } from 'react';

export type LinkUrlErrorResponse = {
  text?: string[];
  url?: string[];
};

export type LinkUrlParams = {
  text: string;
  url: string;
};

interface EditorLinkAddModalProps {
  isOpen: boolean;
  value: LinkUrlParams;
  onChange: (value: LinkUrlParams) => void;
  onSubmit: () => void;
  onClose: () => void;
  errors?: LinkUrlErrorResponse;
  initialValue?: LinkUrlParams;
}

export const EditorLinkAddModal: React.FC<EditorLinkAddModalProps> = ({
  isOpen = false,
  value = { url: '', text: '' },
  onChange,
  onSubmit,
  onClose,
  errors,
  initialValue,
}) => {
  const [isSubmitButtonEnabled, setIsSubmitButtonEnabled] = useState(false);

  const isEditMode = initialValue?.url !== undefined && initialValue.url !== '';

  useEffect(() => {
    if (isEditMode) {
      const hasTextChanged = value.text !== (initialValue?.text || '');
      const hasUrlChanged = value.url !== (initialValue?.url || '');
      setIsSubmitButtonEnabled(hasTextChanged || hasUrlChanged);
    } else {
      setIsSubmitButtonEnabled(value.text.length > 0 && value.url.length > 0);
    }
  }, [value.text, value.url, isEditMode, initialValue?.text, initialValue?.url]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal width='500px'>
      <div className='flex grow flex-col gap-8 px-10 pb-10 pt-[30px] md:gap-4 md:p-6'>
        <div className='border-b border-b-gray-350 pb-2 md:pb-3'>
          <h2 className='text-lg font-semibold leading-loose md:text-xl md:leading-loose'>
            テキストへリンクを追加
          </h2>
        </div>
        <button
          className='closeButton'
          onClick={onClose}
        >
          &times;
        </button>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-[10px]'>
            <h3 className='font-medium'>テキスト</h3>
            <Input
              size='m'
              placeholder=''
              value={value.text}
              error={hasError(errors?.text)}
              onChange={(e) => onChange({ ...value, text: e.target.value })}
            />
            <ErrorMessage>{firstError(errors?.text)}</ErrorMessage>
          </div>
          <div className='flex flex-col gap-[10px]'>
            <h3 className='font-medium'>URL</h3>
            <Input
              size='m'
              placeholder=''
              value={value.url}
              error={hasError(errors?.url)}
              onChange={(e) => onChange({ ...value, url: e.target.value })}
            />
            <ErrorMessage>{firstError(errors?.url)}</ErrorMessage>
          </div>
        </div>
        <div className='mt-auto flex justify-center gap-6 md:gap-4 md:py-[24px]'>
          <div className='w-[172px] md:h-[42px] md:w-[120px]'>
            <Button
              className='md:text-sm'
              variant='secondary'
              fullRounded
              fullWidth
              outline
              onClick={onClose}
            >
              キャンセル
            </Button>
          </div>
          <div className='w-[172px] md:h-[42px] md:w-[120px]'>
            <Button
              className='md:text-sm'
              fullRounded
              fullWidth
              outline={isSubmitButtonEnabled}
              disabled={!isSubmitButtonEnabled}
              onClick={onSubmit}
            >
              保存する
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
